import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Alert } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useNavigate, Link } from 'react-router-dom';
import { FaFileAlt, FaUsers, FaUserGraduate, FaClipboardList, FaCalendarAlt, FaTools, 
         FaChartLine, FaCheckCircle, FaExclamationTriangle, FaClock, FaSignOutAlt } from 'react-icons/fa';
import '../assets/css/Dashboard.css';

const Dashboard = () => {
  const { currentUser, userRole, logout } = useAuth();
  const [userData, setUserData] = useState(null);
  const [stats, setStats] = useState({
    totalTugasAkhir: 0,
    totalBimbingan: 0,
    totalSeminar: 0,
    pendingTasks: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!currentUser) return;

        // Ambil data pengguna
        const userRef = doc(db, "users", currentUser.uid);
        const userDoc = await getDoc(userRef);
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setUserData(userData);
          
          // Fetch stats based on user role
          if (userData.verified) {
            await fetchUserStats(userData.role, currentUser.uid);
          }
        } else {
          setError("Data pengguna tidak ditemukan");
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setError("Terjadi kesalahan saat mengambil data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentUser]);
  
  const fetchUserStats = async (role, userId) => {
    try {
      let statsData = {
        totalTugasAkhir: 0,
        totalBimbingan: 0,
        totalSeminar: 0,
        pendingTasks: 0
      };
      
      // Different stats based on user role
      if (role === 'mahasiswa') {
        // For students, check their own TA status
        const taQuery = query(
          collection(db, "tugas_akhir"),
          where("mahasiswa_id", "==", userId)
        );
        const taSnapshot = await getDocs(taQuery);
        statsData.totalTugasAkhir = taSnapshot.size;
        
        // Count bimbingan sessions
        const bimbinganQuery = query(
          collection(db, "bimbingan"),
          where("mahasiswa_id", "==", userId)
        );
        const bimbinganSnapshot = await getDocs(bimbinganQuery);
        statsData.totalBimbingan = bimbinganSnapshot.size;
        
        // Count seminars
        const seminarQuery = query(
          collection(db, "seminar"),
          where("mahasiswa_id", "==", userId)
        );
        const seminarSnapshot = await getDocs(seminarQuery);
        statsData.totalSeminar = seminarSnapshot.size;
        
        // Count pending tasks (e.g., revisions needed)
        const pendingQuery = query(
          collection(db, "bimbingan"),
          where("mahasiswa_id", "==", userId),
          where("status", "==", "revisi")
        );
        const pendingSnapshot = await getDocs(pendingQuery);
        statsData.pendingTasks = pendingSnapshot.size;
      } 
      else if (role === 'dosen') {
        // For lecturers, count students they supervise
        const taQuery = query(
          collection(db, "tugas_akhir"),
          where("pembimbing_id", "==", userId)
        );
        const taSnapshot = await getDocs(taQuery);
        statsData.totalTugasAkhir = taSnapshot.size;
        
        // Count bimbingan sessions they need to review
        const bimbinganQuery = query(
          collection(db, "bimbingan"),
          where("dosen_id", "==", userId)
        );
        const bimbinganSnapshot = await getDocs(bimbinganQuery);
        statsData.totalBimbingan = bimbinganSnapshot.size;
        
        // Count seminars they need to attend
        const seminarQuery = query(
          collection(db, "seminar"),
          where("penguji_id", "array-contains", userId)
        );
        const seminarSnapshot = await getDocs(seminarQuery);
        statsData.totalSeminar = seminarSnapshot.size;
        
        // Count pending tasks (e.g., bimbingan to review)
        const pendingQuery = query(
          collection(db, "bimbingan"),
          where("dosen_id", "==", userId),
          where("status", "==", "menunggu_review")
        );
        const pendingSnapshot = await getDocs(pendingQuery);
        statsData.pendingTasks = pendingSnapshot.size;
      }
      else if (role === 'pj') {
        // For admin, count all TAs
        const taSnapshot = await getDocs(collection(db, "tugas_akhir"));
        statsData.totalTugasAkhir = taSnapshot.size;
        
        // Count all bimbingan sessions (excluding deleted ones)
        const bimbinganSnapshot = await getDocs(collection(db, "bimbingan"));
        // Filter out deleted bimbingan sessions
        let validBimbinganCount = 0;
        bimbinganSnapshot.forEach(doc => {
          const data = doc.data();
          if (data.deleted !== true) {
            validBimbinganCount++;
          }
        });
        statsData.totalBimbingan = validBimbinganCount;
        
        // Count all seminars
        const seminarSnapshot = await getDocs(collection(db, "seminar"));
        statsData.totalSeminar = seminarSnapshot.size;
        
        // Count pending tasks (e.g., accounts to verify - only those not rejected)
        const pendingQuery = query(
          collection(db, "users"),
          where("verified", "==", false)
        );
        const pendingSnapshot = await getDocs(pendingQuery);
        
        // Filter out rejected users
        let pendingCount = 0;
        pendingSnapshot.forEach(doc => {
          const userData = doc.data();
          if (userData.rejected !== true) {
            pendingCount++;
          }
        });
        statsData.pendingTasks = pendingCount;
      }
      
      setStats(statsData);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error("Error logging out:", error);
      setError("Terjadi kesalahan saat logout");
    }
  };

  // Render dashboard berdasarkan peran pengguna
  const renderDashboard = () => {
    if (!userData) return null;
    
    if (userData.verified === false) {
      return (
        <div className="verification-alert">
          <h4><FaExclamationTriangle className="me-2" /> Akun Belum Diverifikasi</h4>
          <p>Akun Anda belum diverifikasi oleh admin. Silakan tunggu proses verifikasi sebelum dapat mengakses fitur sistem.</p>
        </div>
      );
    }
    
    // Wrap the Row in a div to isolate its margins
    return (
      <div> {/* Added wrapper div */}
        <Row>
          <Col md={4} lg={3} className="mb-4">
            <Card className="profile-card">
            <Card.Header>Profil Pengguna</Card.Header>
            <Card.Body>
              <div className="profile-info">
                <strong>Nama:</strong> {userData.nama}
              </div>
              <div className="profile-info">
                <strong>Email:</strong> {userData.email}
              </div>
              <div className="profile-info">
                <strong>Peran:</strong> {userData.role === 'pj' ? 'Admin' : 
                                      userData.role === 'dosen' ? 'Dosen' : 'Mahasiswa'}
              </div>
              
              {userData.role === 'mahasiswa' && (
                <>
                  <div className="profile-info">
                    <strong>NIM:</strong> {userData.nim}
                  </div>
                  <div className="profile-info">
                    <strong>Program Studi:</strong> {userData.prodi}
                  </div>
                </>
              )}
              
              {userData.role === 'dosen' && (
                <>
                  <div className="profile-info">
                    <strong>NIP:</strong> {userData.nip}
                  </div>
                  <div className="profile-info">
                    <strong>Fakultas:</strong> {userData.fakultas}
                  </div>
                  <div className="profile-info">
                    <strong>Jurusan:</strong> {userData.jurusan}
                  </div>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={8} lg={9}>
          <Card className="welcome-card">
            <Card.Header>Selamat Datang</Card.Header>
            <Card.Body>
              <Card.Title>Sistem Tugas Akhir Rekayasa Kosmetik Itera</Card.Title>
              <Card.Text>
                Selamat datang di Sistem Tugas Akhir Rekayasa Kosmetik Itera. 
                Platform ini dirancang untuk memudahkan proses pengelolaan tugas akhir 
                dari pendaftaran hingga sidang akhir.
              </Card.Text>
            </Card.Body>
          </Card>
          
          <div className="dashboard-stats">
          <h3 className="dashboard-subtitle">Statistik Aktivitas</h3>
            <Row>
              <Col md={6} lg={3} className="mb-4">
                <Link to={userRole === 'pj' ? '/kelola-ta' : userRole === 'dosen' ? '/mahasiswa-bimbingan' : '/pendaftaran-ta'} style={{ textDecoration: 'none' }}>
                  <Card className="stat-card">
                    <Card.Body>
                      <div className="stat-icon blue">
                        <FaFileAlt />
                      </div>
                      <div className="stat-info">
                        <h3>{stats.totalTugasAkhir}</h3>
                        <p>{userRole === 'mahasiswa' ? 'Tugas Akhir' : 
                            userRole === 'dosen' ? 'Mahasiswa Bimbingan' : 
                            'Total Tugas Akhir'}</p>
                      </div>
                    </Card.Body>
                  </Card>
                </Link>
              </Col>
              
              <Col md={6} lg={3} className="mb-4">
                <Link to="/bimbingan" style={{ textDecoration: 'none' }}>
                  <Card className="stat-card">
                    <Card.Body>
                      <div className="stat-icon green">
                        <FaClipboardList />
                      </div>
                      <div className="stat-info">
                        <h3>{stats.totalBimbingan}</h3>
                        <p>Sesi Bimbingan</p>
                      </div>
                    </Card.Body>
                  </Card>
                </Link>
              </Col>
              
              <Col md={6} lg={3} className="mb-4">
                <Link to={userRole === 'pj' ? '/kelola-seminar' : '/seminar'} style={{ textDecoration: 'none' }}>
                  <Card className="stat-card">
                    <Card.Body>
                      <div className="stat-icon orange">
                        <FaCalendarAlt />
                      </div>
                      <div className="stat-info">
                        <h3>{stats.totalSeminar}</h3>
                        <p>Seminar & Sidang</p>
                      </div>
                    </Card.Body>
                  </Card>
                </Link>
              </Col>
              
              <Col md={6} lg={3} className="mb-4">
                <Link to={userRole === 'pj' ? '/verifikasi-akun' : userRole === 'dosen' ? '/pending-assignments' : '/bimbingan'} style={{ textDecoration: 'none' }}>
                  <Card className="stat-card">
                    <Card.Body>
                      <div className="stat-icon red">
                        <FaClock />
                      </div>
                      <div className="stat-info">
                        <h3>{stats.pendingTasks}</h3>
                        <p>{userRole === 'mahasiswa' ? 'Revisi Menunggu' : 
                            userRole === 'dosen' ? 'Menunggu Review' : 
                            'Akun Menunggu Verifikasi'}</p>
                      </div>
                    </Card.Body>
                  </Card>
                </Link>
              </Col>
            </Row>
          </div>
        </Col>
        </Row> {/* Correct closing tag for the main Row */}
      </div>
    );
  };

  // Use standard Container (non-fluid) and apply py-4 padding like VerifikasiAkun
  return (
    <Container className="py-4"> {/* Changed from fluid */}
      {/* Simplified header */}
      <div className="mb-4"> {/* Container handles padding */}
        <h1 className="dashboard-title">Dashboard</h1>
        <p className="dashboard-subtitle">
            Selamat datang, {userData?.nama || 'Pengguna'}! Berikut adalah ringkasan aktivitas Anda.
        </p>
      </div>
        
        {error && <Alert variant="danger">{error}</Alert>}
        
        {loading ? (
          <Alert variant="info">Memuat data...</Alert>
        ) : (
          renderDashboard() 
        )}
    </Container>
  );
};

export default Dashboard;
